import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/sections/PageHero";
import { ContactForm } from "@/components/sections/ContactForm";
import { RelatedLinksSection } from "@/components/sections/RelatedLinksSection";
import { Mail, MapPin, Phone, ArrowUpRight, Clock } from "lucide-react";
import linus from "@/assets/team-linus.webp";
import hero from "@/assets/hero-stockholm.webp";

const KontaktStockholm = () => (
  <>
    <SEO
      title="Redovisningsbyrå i Stockholm – Johanneshov | Cloud"
      description="Redovisningsbyrå på Arenavägen 45 i Johanneshov. Bokföring, lön, bokslut och rådgivning för ägarledda företag i Stockholm."
    />
    <PageHero
      eyebrow="Kontor"
      title={<>Redovisningsbyrå i <span className="italic text-primary">Stockholm</span>.</>}
      description="Vi är en redovisningsbyrå med kontor på Arenavägen 45 i Johanneshov, Stockholm. Vi hjälper ägarledda företag i Stockholmsregionen med bokföring, lön, bokslut och rådgivning, och erbjuder tillfällig förstärkning av ekonomifunktionen. Kontoret leds av Linus Sjögren, redovisningskonsult. Du kan ringa, skriva eller boka ett möte här."
      image={hero}
      imageAlt="Cloud Redovisning kontor i Stockholm"
    />

    <section className="py-20 md:py-28">
      <div className="container-page grid gap-10 lg:grid-cols-12 items-start">
        <div className="lg:col-span-5 bg-card border border-border rounded-sm overflow-hidden shadow-soft">
          <div className="aspect-[5/4] overflow-hidden bg-background-deep">
            <img
              src={linus}
              alt="Linus Sjögren"
              className="h-full w-full object-cover"
              loading="lazy"
              width={1200}
              height={960}
              decoding="async"
            />
          </div>
          <div className="p-8">
            <div className="text-xs uppercase tracking-[0.18em] text-accent font-medium mb-2">Kontorschef</div>
            <h2 className="font-display text-3xl text-foreground">Linus Sjögren</h2>
            <p className="text-foreground-muted mt-1.5">Redovisningskonsult</p>
            <div className="mt-7 space-y-4 pt-7 border-t border-border">
              <a href="tel:0700121211" className="flex items-center gap-3 text-foreground/85 hover:text-primary transition-colors">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-4 w-4 text-primary" />
                </div>
                070-012 12 11
              </a>
              <a href="mailto:linus.sjogren@cloudredovisning.se" className="flex items-center gap-3 text-foreground/85 hover:text-primary transition-colors break-all">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-4 w-4 text-primary" />
                </div>
                linus.sjogren@cloudredovisning.se
              </a>
              <div className="flex items-start gap-3 text-foreground/85">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-4 w-4 text-primary" />
                </div>
                <div>
                  Cloud Redovisning Stockholm AB<br />
                  Arenavägen 45, plan 16<br />
                  121 77 Johanneshov
                </div>
              </div>
              <div className="flex items-center gap-3 text-foreground/85">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-4 w-4 text-primary" />
                </div>
                Mån–fre 08.00–17.00
              </div>
              <a
                href="https://sthlm.cloudredovisning.se/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary mt-2 font-medium"
              >
                Till Cloud Stockholm <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 aspect-[4/5] lg:aspect-auto lg:h-full lg:min-h-[640px] overflow-hidden rounded-sm border border-border bg-background-elevated">
          <iframe
            title="Karta — Arenavägen 45, Johanneshov"
            src="https://www.openstreetmap.org/export/embed.html?bbox=18.05%2C59.28%2C18.13%2C59.31&layer=mapnik&marker=59.295%2C18.082"
            className="h-full w-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>

    <section className="py-16 md:py-20">
      <div className="container-page mx-auto max-w-4xl space-y-14">
        <section>
          <h2 className="font-display text-3xl text-foreground">Våra tjänster för företag i Stockholm</h2>
          <p className="lead mt-5">Vi hjälper företag i Stockholmsregionen med bokföring, löneadministration, bokslut och deklaration samt förstärkning av ekonomifunktionen. Du kan välja en tjänst eller kombinera flera delar.</p>
          <ul className="mt-7 grid gap-6 md:grid-cols-2">
            <li><h3 className="font-display text-xl"><a href="/bokforing-stockholm">Bokföring i Stockholm</a></h3><p className="mt-2 leading-relaxed text-foreground-muted">Digital bokföring för företag som vill följa ekonomin löpande och ha ett tydligt underlag för beslut.</p></li>
            <li><h3 className="font-display text-xl"><a href="/loneadministration">Löneadministration</a></h3><p className="mt-2 leading-relaxed text-foreground-muted">Stöd med löner, arbetsgivardeklarationer (AGI), rapportering och frågor kring lönehantering.</p></li>
            <li><h3 className="font-display text-xl"><a href="/bokslut">Bokslut och deklaration</a></h3><p className="mt-2 leading-relaxed text-foreground-muted">Hjälp med bokslut och deklaration för aktiebolag. Läs även om <a href="/arsredovisning-aktiebolag">årsredovisning för aktiebolag</a>.</p></li>
            <li><h3 className="font-display text-xl"><a href="/inhouse">Inhouse och interim ekonomi</a></h3><p className="mt-2 leading-relaxed text-foreground-muted">Tillfällig förstärkning av ekonomifunktionen, på plats hos er eller i hybridform.</p></li>
          </ul>
        </section>
        <section>
          <h2 className="font-display text-3xl text-foreground">Vi hjälper ägarledda företag i Stockholmsregionen</h2>
          <p className="mt-5 leading-relaxed text-foreground-muted">Vi arbetar med ägarledda företag i Stockholm och den omgivande regionen. Samarbetet kan omfatta löpande redovisning, lönehantering, bokslut eller rådgivning. Företag som vill gå över från en annan byrå kan läsa mer om <a href="/byta-redovisningsbyra">hur ett byte går till</a>. Vi har också huvudkontor i <a href="/kontakt-umea">Umeå</a>.</p>
        </section>
        <section>
          <h2 className="font-display text-3xl text-foreground">Inhouse och interim ger tillfällig förstärkning</h2>
          <p className="mt-5 leading-relaxed text-foreground-muted">När ni behöver förstärka ekonomifunktionen under en period kan en konsult arbeta tillsammans med ert team, på plats eller i hybridform. Upplägget utgår från verksamhetens behov. Läs mer om <a href="/inhouse">inhouse- och interimstöd</a>.</p>
        </section>
        <section>
          <h2 className="font-display text-3xl text-foreground">Så börjar ett samarbete med oss</h2>
          <p className="mt-5 leading-relaxed text-foreground-muted">Första steget är att prata om företaget och vilket stöd ni behöver:</p>
          <ol className="mt-5 list-decimal space-y-3 pl-6 leading-relaxed text-foreground-muted">
            <li>Boka ett möte på kontoret i Johanneshov, på distans eller per telefon.</li>
            <li>Vi går igenom företaget, ert bokföringsprogram och de delar av ekonomiarbetet ni vill ha hjälp med.</li>
            <li>Ni får en offert utifrån verksamhetens behov. Läs mer om <a href="/priser">vad som påverkar priset</a>.</li>
            <li>Om ni vill gå vidare kommer vi överens om ett startdatum och hur arbetet ska lämnas över.</li>
          </ol>
        </section>
        <section>
          <h2 className="font-display text-3xl text-foreground">Vanliga frågor om vår redovisningsbyrå i Stockholm</h2>
          <div className="mt-7 space-y-7">
            <div><h3 className="font-medium">Var ligger Stockholmskontoret?</h3><p className="mt-2 leading-relaxed text-foreground-muted">Vi finns på Arenavägen 45, plan 16, 121 77 Johanneshov. Kontoret är öppet måndag till fredag klockan 08.00–17.00.</p></div>
            <div><h3 className="font-medium">Hjälper ni företag utanför Stockholm?</h3><p className="mt-2 leading-relaxed text-foreground-muted">Ja. Vi hjälper företag i Stockholmsregionen och arbetar digitalt med företag i hela Sverige.</p></div>
            <div><h3 className="font-medium">Vad innebär inhouse- eller interimstöd inom ekonomi?</h3><p className="mt-2 leading-relaxed text-foreground-muted">Det innebär att en konsult tillfälligt förstärker företagets ekonomifunktion, på plats eller i hybridform. Läs mer om <a href="/inhouse">Inhouse-medarbetare</a>.</p></div>
            <div><h3 className="font-medium">Kan ni ta över från min nuvarande redovisningsbyrå?</h3><p className="mt-2 leading-relaxed text-foreground-muted">Ja. Vi kan planera bytet steg för steg tillsammans med er. Läs mer om <a href="/byta-redovisningsbyra">att byta redovisningsbyrå</a>.</p></div>
            <div><h3 className="font-medium">Hur snabbt får jag svar?</h3><p className="mt-2 leading-relaxed text-foreground-muted">Vi svarar inom 24 timmar på vardagar. Om ni vill prata med oss direkt kan ni ringa 070-303 63 45.</p></div>
            <div><h3 className="font-medium">Kan ni hjälpa till med årsredovisning för aktiebolag?</h3><p className="mt-2 leading-relaxed text-foreground-muted">Ja. Vi hjälper till med årsredovisning enligt K2 eller K3. Läs mer om <a href="/arsredovisning-aktiebolag">årsredovisning för aktiebolag</a>.</p></div>
          </div>
        </section>
        <section className="border-t border-border pt-8">
          <h2 className="font-display text-2xl">Vill du träffa oss i Stockholm?</h2>
          <p className="mt-3 leading-relaxed text-foreground-muted">Fyll i formuläret nedan eller ring oss på <a href="tel:0703036345">070-303 63 45</a>. Vi svarar inom 24 timmar på vardagar.</p>
        </section>
      </div>
    </section>

    <RelatedLinksSection
      title="Tjänster som ofta efterfrågas i Stockholmsregionen."
      description="Om ni söker en redovisningsbyrå i Stockholm kan ni börja med rätt tjänsteområde och sedan boka ett första möte med vårt team."
      links={[
        {
          to: "/bokforing",
          title: "Bokföring Stockholm",
          body: "Digital bokföring för små och medelstora företag som vill ha kontroll i realtid.",
        },
        {
          to: "/loneadministration",
          title: "Löneadministration Stockholm",
          body: "Lön, AGI, rapportering och stöd i kollektivavtalsfrågor för växande företag.",
        },
        {
          to: "/bokslut",
          title: "Bokslut & deklaration",
          body: "Hjälp med årsredovisning, deklaration aktiebolag, K10 och skatteplanering.",
        },
        {
          to: "/inhouse",
          title: "Inhouse & interim ekonomi",
          body: "När ni behöver tillfällig förstärkning i ekonomifunktionen på plats eller hybrid.",
        },
      ]}
    />

    <ContactForm title="Skriv till Stockholmskontoret" subtitle="Fyll i formuläret så återkommer vi inom kort." office="Stockholm" />
  </>
);

export default KontaktStockholm;
